import React, { useState } from 'react';
import Layout from '../components/Layout';
import BarcodeScanner from '../components/BarcodeScanner';
import BookVerificationModal from '../components/BookVerificationModal';
import { apiFetch } from '../utils/apiClient';
import { fileToCoverDataUrl } from '../utils/image';
import { useVendor } from '../contexts/VendorContext';
import { Link } from 'react-router-dom';
import SyncResultNotice from '../components/SyncResultNotice';
import CategoryPicker from '../components/CategoryPicker';

const Scanner: React.FC = () => {
  const { isPendingVendor, settings } = useVendor();
  const [isbn, setIsbn] = useState('');
  const [bookLanguage, setBookLanguage] = useState('English');
  const [rfidTag, setRfidTag] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<any>(null);
  const [error, setError] = useState('');
  const [scanMode, setScanMode] = useState<'camera' | 'manual'>('manual');
  const [showVerificationModal, setShowVerificationModal] = useState(false);
  const [pendingIsbn, setPendingIsbn] = useState('');
  
  // Manual entry form fields
  const [title, setTitle] = useState('');
  const [authors, setAuthors] = useState('');
  const [publisher, setPublisher] = useState('');
  const [publicationDate, setPublicationDate] = useState('');
  const [description, setDescription] = useState('');
  const [categories, setCategories] = useState('');
  const [pageCount, setPageCount] = useState('');
  const [sellingPrice, setSellingPrice] = useState('');
  const [condition, setCondition] = useState('new');
  const [quantity, setQuantity] = useState(1);
  const [location, setLocation] = useState('');
  const [notes, setNotes] = useState('');

  const handleScan = async (scannedCode?: string) => {
    const isbnToUse = scannedCode || isbn;
    
    if (!isbnToUse) {
      setError('Please enter or scan an ISBN');
      return;
    }

    // Show verification modal instead of directly scanning
    setPendingIsbn(isbnToUse);
    setShowVerificationModal(true);
  };

  const handleManualSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    
    // Validate required fields
    if (!isbn && !title) {
      setError('Please enter either ISBN or Title');
      return;
    }
    
    if (!sellingPrice) {
      setError('Please enter the selling price');
      return;
    }
    
    setLoading(true);
    
    try {
      const payload: any = {
        isbn: isbn || undefined,
        selling_price: sellingPrice,
        condition: condition,
        quantity: quantity,
      };

      // Add optional book fields if provided
      if (title) payload.title = title;
      if (authors) payload.authors = authors;
      if (publisher) payload.publisher = publisher;
      if (publicationDate) payload.publication_date = publicationDate;
      if (description) payload.description = description;
      if (categories) payload.categories = categories;
      if (pageCount) payload.page_count = parseInt(pageCount) || undefined;
      if (location) payload.location = location;
      if (notes) payload.notes = notes;
      if (rfidTag) payload.rfid_tag = rfidTag;
      payload.language = bookLanguage;

      const response = await apiFetch('/api/scan/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to save inventory item');
      }

      const responseData = await response.json();
      setResult(responseData);
      
      // Reset form
      setIsbn('');
      setRfidTag('');
      setTitle('');
      setAuthors('');
      setPublisher('');
      setPublicationDate('');
      setDescription('');
      setCategories('');
      setPageCount('');
      setSellingPrice('');
      setCondition('new');
      setQuantity(1);
      setLocation('');
      setNotes('');
    } catch (err: any) {
      setError(err.message || 'Failed to save inventory item');
    } finally {
      setLoading(false);
    }
  };

  const handleBarcodeScan = (code: string) => {
    // Code is already normalized to ISBN-13 by BarcodeScanner
    setIsbn(code);
    setError(''); // Clear any previous errors
    // Show verification modal after barcode detection
    setTimeout(() => {
      handleScan(code);
    }, 500);
  };

  const handleSaveToInventory = async (data: {
    isbn: string;
    selling_price: string;
    condition: string;
    quantity: number;
    notes?: string;
    image?: File;
    // Book fields that can be edited
    title?: string;
    authors?: string;
    publisher?: string;
    publication_date?: string;
    page_count?: number;
    description?: string;
    format?: string;
    categories?: string;
    language?: string;
  }) => {
    setLoading(true);
    setError('');

    try {
      const payload: any = {
        isbn: data.isbn,
        selling_price: data.selling_price,
        condition: data.condition,
        quantity: data.quantity,
      };

      // Add optional fields
      if (data.notes) payload.notes = data.notes;
      if (rfidTag) payload.rfid_tag = rfidTag;
      
      // Add book fields if they were edited
      if (data.title) payload.title = data.title;
      if (data.authors) payload.authors = data.authors;
      if (data.publisher) payload.publisher = data.publisher;
      if (data.publication_date) payload.publication_date = data.publication_date;
      if (data.page_count) payload.page_count = data.page_count;
      if (data.description) payload.description = data.description;
      if (data.format) payload.format = data.format;
      if (data.categories) payload.categories = data.categories;
      if (data.language) payload.language = data.language;
      // The seller's photo goes up with the listing and becomes the main
      // product image on BookMarketLk.
      if (data.image) payload.cover_image_data = await fileToCoverDataUrl(data.image);

      const response = await apiFetch('/api/scan/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to save inventory item');
      }

      const responseData = await response.json();
      setResult(responseData);
      setIsbn('');
      setRfidTag('');
      setShowVerificationModal(false);
      setPendingIsbn('');
    } catch (err: any) {
      throw err;
    } finally {
      setLoading(false);
    }
  };

  if (isPendingVendor) {
    return (
      <Layout>
        <div className="max-w-xl mx-auto bg-white shadow rounded-lg p-6 text-center">
          <h1 className="text-xl font-bold text-gray-900">Your store is awaiting approval</h1>
          <p className="mt-2 text-sm text-gray-600">You can scan books once an admin approves your store.</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="w-full max-w-2xl mx-auto">
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4 sm:mb-6">Book Scanner</h1>

        {/* Mode Toggle - Mobile Optimized */}
        <div className="mb-4 sm:mb-6 bg-white shadow rounded-lg p-3 sm:p-4">
          <div className="flex gap-2 sm:gap-3">
            <button
              type="button"
              onClick={() => setScanMode('camera')}
              className={`flex-1 px-3 sm:px-4 py-3 sm:py-2.5 rounded-md font-medium transition-colors text-sm sm:text-base min-h-[44px] touch-manipulation flex items-center justify-center gap-2 ${
                scanMode === 'camera'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-700 active:bg-gray-200'
              }`}
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              Camera
            </button>
            <button
              type="button"
              onClick={() => setScanMode('manual')}
              className={`flex-1 px-3 sm:px-4 py-3 sm:py-2.5 rounded-md font-medium transition-colors text-sm sm:text-base min-h-[44px] touch-manipulation flex items-center justify-center gap-2 ${
                scanMode === 'manual'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-700 active:bg-gray-200'
              }`}
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
              </svg>
              Manual
            </button>
          </div>
        </div>

        {/* Camera Scanner Mode */}
        {scanMode === 'camera' && (
          <div className="bg-white shadow rounded-lg p-4 sm:p-6 mb-4 sm:mb-6">
            <h2 className="text-base sm:text-lg font-semibold text-gray-900 mb-3 sm:mb-4">
              Camera Barcode Scanner
            </h2>
            <BarcodeScanner
              onScan={handleBarcodeScan}
              onError={(err) => setError(err)}
            />
          </div>
        )}

        {/* Manual Input Mode */}
        {scanMode === 'manual' && (
          <div className="bg-white shadow rounded-lg p-4 sm:p-6">
            <h2 className="text-base sm:text-lg font-semibold text-gray-900 mb-3 sm:mb-4">
              Manual Entry
            </h2>
            <form onSubmit={handleManualSubmit} className="space-y-4 sm:space-y-5">
              {/* Book Information Section */}
              <div className="border-b border-gray-200 pb-4">
                <h3 className="text-sm font-semibold text-gray-900 mb-3">Book Information</h3>
                
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="sm:col-span-2">
                    <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-1.5">
                      Title <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      id="title"
                      value={title}
                      onChange={(e) => setTitle(e.target.value)}
                      className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-base sm:text-sm px-3 sm:px-4 py-3 sm:py-2.5 border"
                      placeholder="Book Title"
                      autoComplete="off"
                    />
                  </div>

                  <div>
                    <label htmlFor="isbn" className="block text-sm font-medium text-gray-700 mb-1.5">
                      ISBN
                    </label>
                    <input
                      type="text"
                      id="isbn"
                      value={isbn}
                      onChange={(e) => setIsbn(e.target.value)}
                      className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-base sm:text-sm px-3 sm:px-4 py-3 sm:py-2.5 border"
                      placeholder="9780134685991"
                      autoComplete="off"
                    />
                    <p className="mt-1.5 text-xs text-gray-500">
                      ISBN-10 or ISBN-13 (optional if title provided)
                    </p>
                  </div>

                  <div>
                    <label htmlFor="authors" className="block text-sm font-medium text-gray-700 mb-1.5">
                      Authors
                    </label>
                    <input
                      type="text"
                      id="authors"
                      value={authors}
                      onChange={(e) => setAuthors(e.target.value)}
                      className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-base sm:text-sm px-3 sm:px-4 py-3 sm:py-2.5 border"
                      placeholder="Author Name"
                      autoComplete="off"
                    />
                  </div>

                  <div>
                    <label htmlFor="publisher" className="block text-sm font-medium text-gray-700 mb-1.5">
                      Publisher
                    </label>
                    <input
                      type="text"
                      id="publisher"
                      value={publisher}
                      onChange={(e) => setPublisher(e.target.value)}
                      className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-base sm:text-sm px-3 sm:px-4 py-3 sm:py-2.5 border"
                      placeholder="Publisher Name"
                      autoComplete="off"
                    />
                  </div>

                  <div>
                    <label htmlFor="publicationDate" className="block text-sm font-medium text-gray-700 mb-1.5">
                      Publication Date
                    </label>
                    <input
                      type="date"
                      id="publicationDate"
                      value={publicationDate}
                      onChange={(e) => setPublicationDate(e.target.value)}
                      className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-base sm:text-sm px-3 sm:px-4 py-3 sm:py-2.5 border"
                    />
                  </div>

                  <div>
                    <label htmlFor="pageCount" className="block text-sm font-medium text-gray-700 mb-1.5">
                      Page Count
                    </label>
                    <input
                      type="number"
                      id="pageCount"
                      value={pageCount}
                      onChange={(e) => setPageCount(e.target.value)}
                      className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-base sm:text-sm px-3 sm:px-4 py-3 sm:py-2.5 border"
                      placeholder="300"
                      min="1"
                    />
                  </div>

                  <div>
                    <label htmlFor="bookLanguage" className="block text-sm font-medium text-gray-700 mb-1.5">
                      Book language
                    </label>
                    <select
                      id="bookLanguage"
                      value={bookLanguage}
                      onChange={(e) => setBookLanguage(e.target.value)}
                      className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-base sm:text-sm px-3 sm:px-4 py-3 sm:py-2.5 border min-h-[44px]"
                    >
                      <option value="English">English</option>
                      <option value="Sinhala">Sinhala</option>
                      <option value="Tamil">Tamil</option>
                    </select>
                  </div>

                  <div className="sm:col-span-2">
                    <CategoryPicker value={categories} onChange={setCategories} hint="Pick the store categories this book belongs in." />
                  </div>

                  <div className="sm:col-span-2">
                    <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1.5">
                      Description
                    </label>
                    <textarea
                      id="description"
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      rows={3}
                      className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-base sm:text-sm px-3 sm:px-4 py-2.5 border"
                      placeholder="Book description..."
                    />
                  </div>
                </div>
              </div>

              {/* Inventory Information Section */}
              <div className="border-b border-gray-200 pb-4">
                <h3 className="text-sm font-semibold text-gray-900 mb-3">Inventory Information</h3>
                
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="sellingPrice" className="block text-sm font-medium text-gray-700 mb-1.5">
                      Selling price (Rs.) <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="number"
                      id="sellingPrice"
                      step="0.01"
                      min="0"
                      value={sellingPrice}
                      onChange={(e) => setSellingPrice(e.target.value)}
                      className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-base sm:text-sm px-3 sm:px-4 py-3 sm:py-2.5 border"
                      placeholder="0.00"
                      required
                    />
                  </div>

                  <div>
                    <label htmlFor="condition" className="block text-sm font-medium text-gray-700 mb-1.5">
                      Condition <span className="text-red-500">*</span>
                    </label>
                    <select
                      id="condition"
                      value={condition}
                      onChange={(e) => setCondition(e.target.value)}
                      className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-base sm:text-sm px-3 sm:px-4 py-3 sm:py-2.5 border min-h-[44px]"
                      required
                    >
                      <option value="new">New</option>
                      <option value="like_new">Like New</option>
                      <option value="very_good">Very Good</option>
                      <option value="good">Good</option>
                      <option value="acceptable">Acceptable</option>
                    </select>
                  </div>

                  <div>
                    <label htmlFor="quantity" className="block text-sm font-medium text-gray-700 mb-1.5">
                      Quantity <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="number"
                      id="quantity"
                      min="1"
                      value={quantity}
                      onChange={(e) => setQuantity(parseInt(e.target.value) || 1)}
                      className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-base sm:text-sm px-3 sm:px-4 py-3 sm:py-2.5 border"
                      required
                    />
                  </div>

                  <div>
                    <label htmlFor="location" className="block text-sm font-medium text-gray-700 mb-1.5">
                      Location
                    </label>
                    <input
                      type="text"
                      id="location"
                      value={location}
                      onChange={(e) => setLocation(e.target.value)}
                      className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-base sm:text-sm px-3 sm:px-4 py-3 sm:py-2.5 border"
                      placeholder="A1-B2"
                      autoComplete="off"
                    />
                  </div>

                  <div>
                    <label htmlFor="rfidTag" className="block text-sm font-medium text-gray-700 mb-1.5">
                      RFID Tag
                    </label>
                    <input
                      type="text"
                      id="rfidTag"
                      value={rfidTag}
                      onChange={(e) => setRfidTag(e.target.value)}
                      className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-base sm:text-sm px-3 sm:px-4 py-3 sm:py-2.5 border"
                      placeholder="RFID123456"
                      autoComplete="off"
                    />
                  </div>

                  <div className="sm:col-span-2">
                    <label htmlFor="notes" className="block text-sm font-medium text-gray-700 mb-1.5">
                      Notes
                    </label>
                    <textarea
                      id="notes"
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                      rows={2}
                      className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-base sm:text-sm px-3 sm:px-4 py-2.5 border"
                      placeholder="Additional notes..."
                    />
                  </div>
                </div>
              </div>

              {error && (
                <div className="rounded-md bg-red-50 border border-red-200 p-3 sm:p-4">
                  <p className="text-sm text-red-800">{error}</p>
                </div>
              )}

              <button
                type="submit"
                disabled={loading}
                className="w-full flex justify-center items-center py-3 sm:py-2.5 px-4 border border-transparent rounded-md shadow-sm text-base sm:text-sm font-medium text-white bg-blue-600 active:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 min-h-[44px] touch-manipulation"
              >
                {loading ? (settings.auto_sync ? 'Saving & listing on BookMarketLk...' : 'Saving...') : (settings.auto_sync ? 'Save & list on BookMarketLk' : 'Save to Inventory')}
              </button>
            </form>
          </div>
        )}

        {/* Success Result */}
        {result && (
          <div className="mt-4 sm:mt-6 bg-green-50 border border-green-200 rounded-lg p-4 sm:p-6">
            <div className="flex items-center gap-2 mb-3 sm:mb-4">
              <svg className="w-6 h-6 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <h2 className="text-base sm:text-lg font-semibold text-green-900">
                {result.sync?.status === 'synced' ? 'Book added and listed on BookMarketLk' : 'Book added to inventory'}
              </h2>
            </div>
            <div className="space-y-2 text-sm sm:text-base">
              <p>
                <strong>Title:</strong> {result.book?.title}
              </p>
              <p>
                <strong>ISBN-13:</strong> {result.book?.isbn_13}
              </p>
              <p>
                <strong>Authors:</strong> {result.book?.authors}
              </p>
              <p>
                <strong>Quantity:</strong> {result.inventory_item?.quantity}
              </p>
              <p>
                <strong>Available:</strong>{' '}
                {result.inventory_item?.available_quantity}
              </p>
              <SyncResultNotice sync={result.sync} itemId={result.inventory_item?.id} className="mt-2" />
              <Link to={`/inventory/${result.inventory_item?.id}`} className="inline-block text-blue-600 text-sm font-medium">
                View item →
              </Link>
            </div>
            <button
              onClick={() => {
                setResult(null);
                setIsbn('');
                setRfidTag('');
                setTitle('');
                setAuthors('');
                setPublisher('');
                setPublicationDate('');
                setDescription('');
                setCategories('');
      setCategories('');
                setPageCount('');
                          setSellingPrice('');
                setCondition('new');
                setQuantity(1);
                setLocation('');
                setNotes('');
              }}
              className="mt-4 text-blue-600 active:text-blue-700 text-sm sm:text-base font-medium py-2 min-h-[44px] touch-manipulation flex items-center gap-2"
            >
              Add Another Book
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>
        )}
      </div>

      {/* Book Verification Modal */}
      <BookVerificationModal
        isOpen={showVerificationModal}
        onClose={() => {
          setShowVerificationModal(false);
          setPendingIsbn('');
        }}
        isbn={pendingIsbn}
        onSave={handleSaveToInventory}
      />
    </Layout>
  );
};

export default Scanner;
